import React, { useEffect, useMemo, useState } from 'react';

import { DeleteOutlined, EditOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Popconfirm, Row, Select, Space, Switch, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { showErrorMessage } from '../../../../helpers/utils/ui';
import { adsCabinetApi } from '../../../../shared/common/api/endpoints/adsCabinetApi';
import { unApi } from '../../../../shared/common/api/endpoints/reports/unApi';
import { useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';

const { Text } = Typography;

interface Props {
    mode:number
    addModalVisible:boolean
    refetch:boolean
    handleEditTask:(taskId:number)=>void
    onCreateTask?: () => void
    onConnectMetric?: () => void
}

interface ITaskFilters {
    task_name: string;
    task_type_name: string;
    counter_id: string;
    counter_name: string;
    object_type: string;
    target_label: string;
    conversion_session_name: string;
    status: string;
}

interface ICounterFilters {
    login: string;
    counter_id: string;
    counter_name: string;
    status: string;
}

const initialTaskFilters: ITaskFilters = {
    task_name: '',
    task_type_name: '',
    counter_id: '',
    counter_name: '',
    object_type: '',
    target_label: '',
    conversion_session_name: '',
    status: '',
};

const initialCounterFilters: ICounterFilters = {
    login: '',
    counter_id: '',
    counter_name: '',
    status: '',
};

const YandexTable = ({ mode,addModalVisible,refetch:parentRefetch,handleEditTask, onCreateTask, onConnectMetric }:Props) => {
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    const [ tableData,setTableData ] = useState<any[]>([]);
    const [ isLoading,setIsLoading ] = useState(false);
    const [ refetch,setRefetch ] = useState(false);
    const [ showFilters, setShowFilters ] = useState(false);
    const [ taskFilters, setTaskFilters ] = useState<ITaskFilters>(initialTaskFilters);
    const [ counterFilters, setCounterFilters ] = useState<ICounterFilters>(initialCounterFilters);

    const getTableData =  async () => {
        try{
            setIsLoading(true);
            switch (mode){
                case 1: {
                    const { data } = await unApi.getYandexTasks(Number(selectedSchool?.id));
                    setTableData(data);
                    break;
                }
                case 2:{
                    const { data } = await unApi.getYandexMetrics(Number(selectedSchool?.id));
                    setTableData(data);
                    break;
                }
            }

        }catch(err){
            showErrorMessage('Произошла ошибка , повторите попытку позже.');
        }
        finally{
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getTableData();
    },[ mode,selectedSchool,refetch,addModalVisible,parentRefetch ]);

    const handleTaskFilterChange = (field: keyof ITaskFilters, value: string) => {
        setTaskFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleCounterFilterChange = (field: keyof ICounterFilters, value: string) => {
        setCounterFilters(prev => ({ ...prev, [field]: value }));
    };

    const resetTaskFilters = () => {
        setTaskFilters(initialTaskFilters);
    };

    const resetCounterFilters = () => {
        setCounterFilters(initialCounterFilters);
    };

    // Получаем уникальные значения для каждого поля (задачи)
    const uniqueTaskValues = useMemo(() => {
        const getUniqueValues = (field: string): string[] => {
            const values = tableData
                .map((item: any) => item[field])
                .filter((value): value is string => Boolean(value) && value !== '');
            return [...new Set(values)].sort();
        };

        const getUniqueCounterIds = (): string[] => {
            const values = tableData
                .map((item: any) => item.counter_id)
                .filter((value): value is number => value !== null && value !== undefined);
            return [...new Set(values)].map(String).sort((a, b) => Number(a) - Number(b));
        };

        return {
            task_name: getUniqueValues('task_name'),
            task_type_name: getUniqueValues('task_type_name'),
            counter_id: getUniqueCounterIds(),
            counter_name: getUniqueValues('counter_name'),
            object_type: getUniqueValues('object_type'),
            target_label: getUniqueValues('target_label'),
            conversion_session_name: getUniqueValues('conversion_session_name'),
            status: ['Включено', 'Выключено'],
        };
    }, [tableData]);

    // Получаем уникальные значения для каждого поля (счётчики)
    const uniqueCounterValues = useMemo(() => {
        const getUniqueValues = (field: string): string[] => {
            const values = tableData
                .map((item: any) => item[field])
                .filter((value): value is string => Boolean(value) && value !== '');
            return [...new Set(values)].sort();
        };

        const getUniqueCounterIds = (): string[] => {
            const values = tableData
                .map((item: any) => item.counter_id)
                .filter((value): value is number => value !== null && value !== undefined);
            return [...new Set(values)].map(String).sort((a, b) => Number(a) - Number(b));
        };

        return {
            login: getUniqueValues('login'),
            counter_id: getUniqueCounterIds(),
            counter_name: getUniqueValues('counter_name'),
            status: ['Включено', 'Выключено'],
        };
    }, [tableData]);

    const filteredData = useMemo(() => {
        if (mode === 1) {
            return tableData.filter((item: any) => {
                const matchTaskName = !taskFilters.task_name || item.task_name === taskFilters.task_name;
                const matchTaskTypeName = !taskFilters.task_type_name || item.task_type_name === taskFilters.task_type_name;
                const matchCounterId = !taskFilters.counter_id || String(item.counter_id) === taskFilters.counter_id;
                const matchCounterName = !taskFilters.counter_name || item.counter_name === taskFilters.counter_name;
                const matchObjectType = !taskFilters.object_type || item.object_type === taskFilters.object_type;
                const matchTargetLabel = !taskFilters.target_label || item.target_label === taskFilters.target_label;
                const matchConversionSessionName = !taskFilters.conversion_session_name || item.conversion_session_name === taskFilters.conversion_session_name;
                const matchStatus = !taskFilters.status || 
                    (taskFilters.status === 'Включено' && item.status === true) ||
                    (taskFilters.status === 'Выключено' && item.status === false);

                return matchTaskName && matchTaskTypeName && matchCounterId && matchCounterName && 
                       matchObjectType && matchTargetLabel && matchConversionSessionName && matchStatus;
            });
        } else if (mode === 2) {
            return tableData.filter((item: any) => {
                const matchLogin = !counterFilters.login || item.login === counterFilters.login;
                const matchCounterId = !counterFilters.counter_id || String(item.counter_id) === counterFilters.counter_id;
                const matchCounterName = !counterFilters.counter_name || item.counter_name === counterFilters.counter_name;
                const matchStatus = !counterFilters.status || 
                    (counterFilters.status === 'Включено' && item.status === true) ||
                    (counterFilters.status === 'Выключено' && item.status === false);

                return matchLogin && matchCounterId && matchCounterName && matchStatus;
            });
        }
        return tableData;
    }, [tableData, taskFilters, counterFilters, mode]);

    const hasActiveTaskFilters = Object.values(taskFilters).some(v => v !== '');
    const hasActiveCounterFilters = Object.values(counterFilters).some(v => v !== '');

    const modeColumnsMap:{
        [key: number]: ColumnsType<any>;
    } = {
        1:[
            {
                title:'ID',
                key:'id',
                dataIndex:'id',
                sorter: (a, b) => a.id - b.id,
            },
            {
                title:'Название задачи',
                key:'task_name',
                dataIndex:'task_name',
                sorter: (a, b) => (a.task_name || '').localeCompare(b.task_name || ''),
            },
            {
                title: 'Что делаем',
                dataIndex: 'task_type_name',
                key: 'task_type_name',
                sorter: (a, b) => (a.task_type_name || '').localeCompare(b.task_type_name || ''),
            },
            {
                title: 'ID счётчика',
                dataIndex: 'counter_id',
                key: 'counter_id',
                sorter: (a, b) => (a.counter_id || 0) - (b.counter_id || 0),
            },
            {
                title: 'Название счётчика',
                dataIndex: 'counter_name',
                key: 'counter_name',
                sorter: (a, b) => (a.counter_name || '').localeCompare(b.counter_name || ''),
            },
            {
                title: 'Что отправляем?',
                dataIndex: 'object_type',
                key: 'object_type',
                sorter: (a, b) => (a.object_type || '').localeCompare(b.object_type || ''),
            },
            {
                title: 'Название цели',
                dataIndex: 'target_label',
                key: 'target_label',
                sorter: (a, b) => (a.target_label || '').localeCompare(b.target_label || ''),
            },
            {
                title: 'Привязка к сессии',
                dataIndex: 'conversion_session_name',
                key: 'conversion_session_name',
                sorter: (a, b) => (a.conversion_session_name || '').localeCompare(b.conversion_session_name || ''),
            },
            {
                title:'Ошибка',
                dataIndex:'msg',
                key:'msg'
            },
            {
                title:'Действия',
                dataIndex:'actions',
                key:'actions',
                render: (value, record) => {
                    return (
                        <Space>
                            <Switch
                                checked={record.status == true}
                                onChange={(val) => {
                                    if (selectedSchool) {
                                        const update = async () => {
                                            setIsLoading(true);
                                            await adsCabinetApi.updateStatusYandex(
                                                parseInt(selectedSchool.id),
                                                record.id,
                                                val,
                                                true
                                            );
                                            setIsLoading(false);
                                            setRefetch((prev)=>!prev);
                                        };
                                        update();
                                    }
                                }}
                            />
                            <Popconfirm
                                title="Вы уверены, что хотите удалить задачу?"
                                onConfirm={async () => {
                                    await unApi.removeYandexTask(record.id);
                                    setRefetch((prev) => !prev);
                                }}
                                okText="Да"
                                cancelText="Нет"
                            >
                                <Button
                                    danger
                                    type="default"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                />
                            </Popconfirm>
                            <Button
                                type="default"
                                size="small"
                                onClick={()=>{handleEditTask(record.id);}}
                                icon={<EditOutlined />}
                            />
                        </Space>
                    );
                },
            },
        ],
        2:[
            {
                title:'Логин',
                key:'login',
                dataIndex:'login'
            },
            {
                title: '№ Счётчика',
                dataIndex: 'counter_id',
                key: 'counter_id',
            },
            {
                title: 'Название счётчика',
                dataIndex: 'counter_name',
                key: 'counter_name',
                width:'12%'
            },
            {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                render: (value, record) => (
                    <Switch
                        checked={value == true}
                        onChange={(value) => {
                            if (selectedSchool) {
                                const update = async () => {
                                    setIsLoading(true);
                                    await adsCabinetApi.updateStatusYandex(
                                        parseInt(selectedSchool.id),
                                        record.id,
                                        value,
                                        false
                                    );
                                    setIsLoading(false);
                                    setRefetch((prev)=>!prev);
                                };
                                update();
                            }
                        }}
                    />
                ),
            },
            {
                title:'Ошибка',
                dataIndex:'msg',
                key:'msg'
            },
            {
                title:'Действия',
                dataIndex:'cab_actions',
                key:'cab_actions',
                render: (value, record) => {
                    return (
                        <div>
                            <Popconfirm
                                title={record?.checking_tasks ? 'У данного счётчика есть задачи , сначала удалите их.' : 'Вы уверены, что хотите удалить счётчик?'}
                                onConfirm={async () => {
                                    record?.checking_tasks ? showErrorMessage('Перед удалением счётчика удалите его задачи.') :  await adsCabinetApi.removeYandexCounter(record.id);
                                        setRefetch((prev) => !prev);
                                }}
                                okText="Да"
                                cancelText="Нет"
                            >
                                <Button
                                    danger
                                    type="default"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                />
                            </Popconfirm>
                        </div>
                    );
                },
            },
        ]
    };

    return (
        <div>
            {/* Фильтры для задач (mode === 1) */}
            {mode === 1 && (
                <>
                    <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                                if (showFilters) {
                                    resetTaskFilters();
                                }
                                setShowFilters(!showFilters);
                            }}
                        >
                            {showFilters ? 'Очистить и скрыть фильтры' : 'Показать фильтры'}
                        </Text>
                        {onCreateTask && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={onCreateTask}
                                style={{ 
                                    backgroundColor: '#ff4d4f',
                                    borderColor: '#ff4d4f',
                                    color: '#fff',
                                    borderRadius: '5px',
                                }}
                            >
                                Создать задачу
                            </Button>
                        )}
                    </div>

                    {showFilters && (
                        <Card 
                            size="small" 
                            style={{ marginBottom: 24, border: '1px solid #d9d9d9', borderRadius: '8px' }}
                        >
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Название задачи</Text>
                                            <Select
                                                value={taskFilters.task_name || undefined}
                                                onChange={(value) => handleTaskFilterChange('task_name', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueTaskValues.task_name.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Что делаем</Text>
                                            <Select
                                                value={taskFilters.task_type_name || undefined}
                                                onChange={(value) => handleTaskFilterChange('task_type_name', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueTaskValues.task_type_name.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>ID счётчика</Text>
                                            <Select
                                                value={taskFilters.counter_id || undefined}
                                                onChange={(value) => handleTaskFilterChange('counter_id', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueTaskValues.counter_id.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Название счётчика</Text>
                                            <Select
                                                value={taskFilters.counter_name || undefined}
                                                onChange={(value) => handleTaskFilterChange('counter_name', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueTaskValues.counter_name.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                </Row>
                                <Row gutter={[16, 16]}>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Что отправляем?</Text>
                                            <Select
                                                value={taskFilters.object_type || undefined}
                                                onChange={(value) => handleTaskFilterChange('object_type', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueTaskValues.object_type.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Название цели</Text>
                                            <Select
                                                value={taskFilters.target_label || undefined}
                                                onChange={(value) => handleTaskFilterChange('target_label', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueTaskValues.target_label.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Привязка к сессии</Text>
                                            <Select
                                                value={taskFilters.conversion_session_name || undefined}
                                                onChange={(value) => handleTaskFilterChange('conversion_session_name', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueTaskValues.conversion_session_name.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Статус</Text>
                                            <Select
                                                value={taskFilters.status || undefined}
                                                onChange={(value) => handleTaskFilterChange('status', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueTaskValues.status.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                </Row>
                                {hasActiveTaskFilters && (
                                    <Row>
                                        <Col>
                                            <Button 
                                                onClick={resetTaskFilters}
                                                style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                                            >
                                                Очистить фильтры
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                            </Space>
                        </Card>
                    )}
                </>
            )}

            {/* Фильтры для счётчиков (mode === 2) */}
            {mode === 2 && (
                <>
                    <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                                if (showFilters) {
                                    resetCounterFilters();
                                }
                                setShowFilters(!showFilters);
                            }}
                        >
                            {showFilters ? 'Очистить и скрыть фильтры' : 'Показать фильтры'}
                        </Text>
                        {onConnectMetric && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={onConnectMetric}
                                style={{ 
                                    backgroundColor: '#ff4d4f',
                                    borderColor: '#ff4d4f',
                                    color: '#fff',
                                    borderRadius: '5px',
                                }}
                            >
                                Подключить метрику
                            </Button>
                        )}
                    </div>

                    {showFilters && (
                        <Card 
                            size="small" 
                            style={{ marginBottom: 24, border: '1px solid #d9d9d9', borderRadius: '8px' }}
                        >
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Логин</Text>
                                            <Select
                                                value={counterFilters.login || undefined}
                                                onChange={(value) => handleCounterFilterChange('login', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueCounterValues.login.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>№ Счётчика</Text>
                                            <Select
                                                value={counterFilters.counter_id || undefined}
                                                onChange={(value) => handleCounterFilterChange('counter_id', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueCounterValues.counter_id.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Название счётчика</Text>
                                            <Select
                                                value={counterFilters.counter_name || undefined}
                                                onChange={(value) => handleCounterFilterChange('counter_name', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueCounterValues.counter_name.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={6}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Статус</Text>
                                            <Select
                                                value={counterFilters.status || undefined}
                                                onChange={(value) => handleCounterFilterChange('status', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueCounterValues.status.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                </Row>
                                {hasActiveCounterFilters && (
                                    <Row>
                                        <Col>
                                            <Button 
                                                onClick={resetCounterFilters}
                                                style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                                            >
                                                Очистить фильтры
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                            </Space>
                        </Card>
                    )}
                </>
            )}

            <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
                <Table
                    columns={modeColumnsMap[mode]}
                    dataSource={filteredData}
                    rowKey="id"
                    loading={{
                        spinning: isLoading,
                        indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
                    }}
                    bordered
                    scroll={{ x: 1200 }}
                    pagination={{
                        showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                    }}
                />
            </div>
        </div>
    );

};
export default YandexTable;
